// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type MembershipOption = {
  id: string;
  name: string;
  isFriend?: boolean;
};

type MembershipRowLike =
  | { kind: 'existing'; playerId: string }
  | { kind: 'draft'; rowId: string; selectedPlayerId?: string };

type MembershipDraftStateLike = {
  query: string;
  selectedPlayerId?: string;
  temporarySelected: boolean;
  status: 'idle' | 'pending';
};

export const normalizeMembershipQuery = (value: string) => value.trim().toLowerCase();

export const hasExactMembershipNameMatch = (options: MembershipOption[], query: string) => {
  const normalizedQuery = normalizeMembershipQuery(query);
  if (!normalizedQuery) return false;
  return options.some((option) => normalizeMembershipQuery(option.name) === normalizedQuery);
};

export const buildMembershipOptionsByQuery = (
  options: MembershipOption[],
  query: string,
  limit = 8
) => {
  const normalizedQuery = normalizeMembershipQuery(query);
  return options
    .filter((option) => normalizeMembershipQuery(option.name).includes(normalizedQuery))
    .sort((left, right) => {
      const leftFriend = left.isFriend === true;
      const rightFriend = right.isFriend === true;
      if (leftFriend !== rightFriend) {
        return leftFriend ? -1 : 1;
      }
      const byName = left.name.localeCompare(right.name);
      if (byName !== 0) {
        return byName;
      }
      return left.id.localeCompare(right.id);
    })
    .slice(0, limit);
};

export const buildMembershipDraftOptions = (
  rows: MembershipRowLike[],
  rowId: string,
  availableMembers: MembershipOption[],
  query: string,
  selectedPlayerId?: string,
  limit = 8
) => {
  const reservedIds = new Set(
    rows.flatMap((candidate) => {
      if (candidate.kind === 'existing') return [candidate.playerId];
      if (candidate.rowId !== rowId && candidate.selectedPlayerId) return [candidate.selectedPlayerId];
      return [];
    })
  );
  const availableOptions = availableMembers.filter(
    (member) => !reservedIds.has(member.id) || member.id === selectedPlayerId
  );
  return buildMembershipOptionsByQuery(availableOptions, query, limit);
};

export const shouldShowTemporaryToggle = (
  draft: Pick<MembershipDraftStateLike, 'query' | 'selectedPlayerId' | 'temporarySelected'>,
  hasExactMatch: boolean
) =>
  draft.temporarySelected ||
  (draft.query.trim().length > 0 && !draft.selectedPlayerId && !hasExactMatch);

export const canConfirmMembershipDraft = (
  draft: MembershipDraftStateLike,
  saving: boolean,
  hasExactMatch: boolean
) =>
  !saving &&
  draft.status !== 'pending' &&
  (Boolean(draft.selectedPlayerId) ||
    (draft.temporarySelected && draft.query.trim().length > 0 && !hasExactMatch));
